import { useEffect, useState } from 'react'

import { WithSuspenseBoundary } from '@/shared/api'
import { DateRange } from '@/shared/lib'
import { Dialog } from '@/shared/ui'

import { AvailableWeekCalendar } from './available-week-calendar'
import {
  AvailableWeekCalendarFallback,
  CalendarLoadingFallback,
  ScheduleCalendarFallback,
  CalendarDialogErrorFallback,
  CalendarDialogLoadingFallback,
} from './fallback'
import { ScheduleCalendar } from './schedule-calendar'
import { useContractPeriod } from '../lib/contract-period'

type Props = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  roomId: number
  period?: DateRange
  onSelect: (dateRange: DateRange) => void
}

export const PeriodCalendarDialog = ({
  isOpen,
  onOpenChange,
  roomId,
  period,
  onSelect,
}: Props) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className="h-[564px] w-fit flex-row gap-0 p-0">
        <Dialog.Header>
          <Dialog.Title className="sr-only">이용 기간 선택하기</Dialog.Title>
        </Dialog.Header>
        <WithSuspenseBoundary
          error={<CalendarDialogErrorFallback />}
          loading={<CalendarDialogLoadingFallback />}
          active
        >
          <Content roomId={roomId} period={period} onSelect={onSelect} />
        </WithSuspenseBoundary>
      </Dialog.Content>
    </Dialog.Root>
  )
}

type ContentProps = {
  period?: DateRange
  onSelect: (dateRange: DateRange) => void
  roomId: number
}

const Content = ({ onSelect, roomId, period: defaultPeriod }: ContentProps) => {
  const [initialLoading, setInitialLoading] = useState(true)

  const { period, updatePeriod } = useContractPeriod({ defaultPeriod })

  const handleStartDateSelect = (date?: Date) => {
    updatePeriod({
      from: date,
      to: undefined,
    })
  }

  const handleEndDateSelect = (date: Date) => {
    if (date) {
      const range = {
        from: period.from,
        to: date,
      }

      onSelect(range)
    }
  }

  useEffect(() => {
    setInitialLoading(false)
  }, [])

  return (
    <>
      <WithSuspenseBoundary
        active={!initialLoading}
        error={<ScheduleCalendarFallback />}
        loading={<CalendarLoadingFallback />}
      >
        <ScheduleCalendar
          roomId={roomId}
          startDate={period.from}
          onSelect={handleStartDateSelect}
        />
      </WithSuspenseBoundary>
      <WithSuspenseBoundary
        active={!initialLoading}
        error={<AvailableWeekCalendarFallback />}
        loading={<CalendarLoadingFallback />}
      >
        <AvailableWeekCalendar
          roomId={roomId}
          startDate={period.from}
          endDate={period.to}
          onSelect={handleEndDateSelect}
        />
      </WithSuspenseBoundary>
    </>
  )
}
